// Contrail V3.8.0 — Commands panel in Admin → Devices → a device.
// Sends data-channel commands through the tracking server (Traccar) and shows the history with live replies.
// All safety rules are enforced by /api/admin (role, verified presets, engine stop only when stopped, one queued at a time);
// this panel mirrors them so staff see why a button is unavailable.
import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { adminApi, presetFits, type AdminDevice, type CommandInfo, type CommandPreset, type DeviceCommand, type StaffRole } from "../../lib/admin";
import { ago } from "../../lib/format";
import { Notice, fmtDateTime } from "./ui";

const STATUS: Record<DeviceCommand["status"], { label: string; tone: string }> = {
  sent: { label: "Sent · waiting for reply", tone: "info" },
  queued: { label: "Queued · goes out when the tracker connects", tone: "warn" },
  replied: { label: "Replied", tone: "ok" },
  no_reply: { label: "No reply", tone: "warn" },
  failed: { label: "Not sent", tone: "bad" },
};

export default function DeviceCommands({ device, role }: { device: AdminDevice; role: StaffRole }) {
  const admin = role === "skyward_admin";
  const [info, setInfo] = useState<CommandInfo | null>(null);
  const [infoErr, setInfoErr] = useState<string | null>(null);
  const [presets, setPresets] = useState<CommandPreset[] | null>(null);
  const [history, setHistory] = useState<DeviceCommand[] | null>(null);
  const [open, setOpen] = useState<string | null>(null);   // preset id or "raw"
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const simulated = !!(device as any).is_simulated;

  const loadHistory = useCallback(async () => {
    const { data } = await supabase.from("device_commands").select("*").eq("device_id", device.id).order("created_at", { ascending: false }).limit(20);
    setHistory((data ?? []) as DeviceCommand[]);
  }, [device.id]);

  useEffect(() => {
    if (simulated) return;
    adminApi<CommandInfo>("GET", `devices/${device.id}/commands`).then(setInfo).catch((e) => setInfoErr(e.message));
    supabase.from("command_presets").select("*").eq("active", true).order("sort").then(({ data }) => setPresets((data ?? []) as CommandPreset[]));
    loadHistory();
    // live replies (Realtime), with a slow poll as a fallback
    const ch = supabase.channel(`cmd-${device.id}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "device_commands", filter: `device_id=eq.${device.id}` }, () => loadHistory())
      .subscribe();
    const t = setInterval(loadHistory, 15_000);
    return () => { clearInterval(t); supabase.removeChannel(ch); };
  }, [device.id, simulated, loadHistory]);

  const fits = useMemo(() => (presets ?? []).filter((p) => p.channel === "data" && presetFits(p, device)), [presets, device]);
  const queued = history?.find((h) => h.status === "queued" && Date.now() - Date.parse(h.created_at) < 24 * 3600_000);

  if (simulated) return <div className="wide cmd-panel"><h3>Commands</h3><p className="sub">Demo devices can't receive commands.</p></div>;

  const allowed = (p: CommandPreset) => admin || (p.verified && p.tech_allowed && !p.danger);
  const supported = (p: CommandPreset) => !info?.types.length || p.type === "custom" || info.types.includes(p.type);

  return (
    <div className="wide cmd-panel">
      <div className="cmd-head">
        <h3>Commands</h3>
        {info && <span className={`cmd-conn ${info.online ? "on" : ""}`}>{info.online ? "Connected now" : info.status === "unknown" ? "Not connected" : `Not connected (${info.status})`}</span>}
      </div>
      {infoErr && <Notice kind="warn">Couldn't reach the tracking server: {infoErr}</Notice>}
      {info?.note && <p className="sub">{info.note}</p>}
      {info && !info.online && !info.note && <p className="sub">Commands sent now wait in a queue on the tracking server and go out the next time this tracker connects{info.last_update ? ` (last contact ${ago(info.last_update)})` : ""}.</p>}
      {queued && <Notice kind="warn">“{queued.label}” is waiting for the tracker to connect. Send the next command after it has gone out.</Notice>}
      {msg && <Notice kind={msg.ok ? "ok" : "bad"} onClose={() => setMsg(null)}>{msg.text}</Notice>}

      {presets === null ? <p className="sub">Loading commands…</p> : !fits.length ? (
        <p className="sub">No commands are set up for {device.model ?? "this model"} ({device.protocol ?? "unknown protocol"}).{admin ? " Add one under Command presets." : ""}</p>
      ) : (
        <div className="cmd-list">
          {fits.map((p) => (
            <div key={p.id} className={`cmd-item ${open === p.id ? "open" : ""}`}>
              <button type="button" className="cmd-btn" disabled={!allowed(p) || !supported(p)} onClick={() => { setMsg(null); setOpen(open === p.id ? null : p.id); }}
                title={!allowed(p) ? "Only Skyward admins can send this" : !supported(p) ? "The tracking server says this tracker doesn't support it" : p.description ?? undefined}>
                <b>{p.label}</b>
                {p.danger && <span className="apill bad">Engine</span>}
                {!p.verified && <span className="apill warn">Not verified</span>}
                {!supported(p) && <span className="apill plain">Not supported</span>}
              </button>
              {open === p.id && <SendForm device={device} preset={p} online={!!info?.online}
                onDone={(text, ok) => { setMsg({ ok, text }); if (ok) setOpen(null); loadHistory(); }} />}
            </div>
          ))}
        </div>
      )}
      {admin && (
        <div className={`cmd-item ${open === "raw" ? "open" : ""}`}>
          <button type="button" className="cmd-btn linkish-btn" onClick={() => { setMsg(null); setOpen(open === "raw" ? null : "raw"); }}>Raw command…</button>
          {open === "raw" && <SendForm device={device} preset={null} online={!!info?.online} onDone={(text, ok) => { setMsg({ ok, text }); if (ok) setOpen(null); loadHistory(); }} />}
        </div>
      )}

      <h4 className="cmd-hist-title">History</h4>
      {history === null ? <p className="sub">Loading…</p> : !history.length ? <p className="sub">No commands sent yet.</p> : (
        <ul className="cmd-hist">
          {history.map((h) => (
            <li key={h.id}>
              <div className="cmd-hist-top">
                <b>{h.label}</b>
                <span className={`apill ${STATUS[h.status].tone}`}>{STATUS[h.status].label}</span>
              </div>
              <div className="sub">{fmtDateTime(h.created_at)}{h.sent_by_email ? ` · ${h.sent_by_email}` : ""}{h.reason ? ` · ${h.reason}` : ""}</div>
              {h.result && <div className="cmd-result mono">{h.result}<span className="sub"> · {fmtDateTime(h.result_at)}</span></div>}
              {h.error && <div className="error">{h.error}</div>}
              {h.type === "custom" && typeof h.attributes?.data === "string" && <div className="sub mono">{String(h.attributes.data)}</div>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function SendForm({ device, preset, online, onDone }: { device: AdminDevice; preset: CommandPreset | null; online: boolean; onDone: (text: string, ok: boolean) => void }) {
  const [vals, setVals] = useState<Record<string, string>>(() => Object.fromEntries((preset?.params ?? []).map((p) => [p.key, String(p.default ?? "")])));
  const [raw, setRaw] = useState("*HQ,{uniqueId},");
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState("");
  const [noQueue, setNoQueue] = useState(false);
  const [busy, setBusy] = useState(false);
  const danger = !!preset?.danger;
  const needReason = !preset || danger || !preset.verified;
  const kmh = Math.round(Number(device.device_state?.speed_kn ?? 0) * 1.852);

  async function send(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const body = preset ? { preset_id: preset.id, params: vals, reason: reason || undefined, confirm: danger ? confirm : undefined, no_queue: noQueue || undefined }
        : { raw, reason, no_queue: noQueue || undefined };
      const r = await adminApi<DeviceCommand>("POST", `devices/${device.id}/commands`, body);
      onDone(r.status === "queued" ? `Queued “${r.label}”. It goes out when ${device.name} next connects.` : `Sent “${r.label}” to ${device.name}. The reply will appear below.`, true);
    } catch (x: any) { onDone(x.message, false); }
    finally { setBusy(false); }
  }

  return (
    <form className="cmd-form" onSubmit={send}>
      {preset?.description && <p className="sub">{preset.description}</p>}
      {preset && !preset.verified && <Notice kind="warn">Not verified on {preset.model ?? "this model"} yet. Send it on a bench unit or a parked vehicle first, then mark the preset Verified.</Notice>}
      {danger && <Notice kind="bad">This cuts the engine. It is only sent if {device.name} is stopped (last report {kmh} km/h) and connected right now. It is never queued.</Notice>}
      {(preset?.params ?? []).map((p) => (
        <label key={p.key} className="cmd-field"><span>{p.label}{p.unit ? ` (${p.unit})` : ""}</span>
          <input type={p.kind === "number" ? "number" : "text"} min={p.min} max={p.max} required value={vals[p.key] ?? ""} onChange={(e) => setVals({ ...vals, [p.key]: e.target.value })} />
        </label>
      ))}
      {!preset && (
        <label className="cmd-field wide"><span>Text sent to the tracker</span>
          <input className="mono" value={raw} onChange={(e) => setRaw(e.target.value)} required maxLength={200} />
          <small>{"{uniqueId}"} becomes the tracker's ID, {"{time}"} the time (HHMMSS UTC). H02 trackers expect *HQ,…#.</small>
        </label>
      )}
      {needReason && (
        <label className="cmd-field wide"><span>Reason</span>
          <input value={reason} onChange={(e) => setReason(e.target.value)} required minLength={3} maxLength={300} placeholder={danger ? "e.g. reported stolen, owner asked (ticket 1234)" : "e.g. bench test"} />
        </label>
      )}
      {danger && (
        <label className="cmd-field wide"><span>Type <b>{device.name}</b> to confirm</span>
          <input value={confirm} onChange={(e) => setConfirm(e.target.value)} required autoComplete="off" />
        </label>
      )}
      {!danger && !online && (
        <label className="acheck"><input type="checkbox" checked={noQueue} onChange={(e) => setNoQueue(e.target.checked)} /> Only send if the tracker is connected now (don't queue)</label>
      )}
      <div><button className={danger ? "btn-danger" : "btn-primary"} disabled={busy || (danger && confirm.trim().toLowerCase() !== device.name.trim().toLowerCase())}>
        {busy ? "Sending…" : danger ? "Stop engine" : "Send"}</button></div>
    </form>
  );
}
